import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ratingsApi } from '@/features/ratings/api/ratingsApi';
import { Rating } from '@/types/rating';

/**
 * Custom hook for managing place ratings with TanStack Query.
 */
export const usePlaceRating = (userId: string | null, placeId: string) => {
  const queryClient = useQueryClient();

  // Fetch user's ratings
  const { data: userRatings = [], isLoading } = useQuery<Rating[]>({
    queryKey: ['userRatings', userId],
    queryFn: () => ratingsApi.getUserRatings(userId!),
    enabled: !!userId, // Only run if userId exists
    retry: 1,
  });

  // Get user's rating for this place
  const userRating = userRatings.find((r) => r.placeId === placeId);

  // Submit/update rating mutation
  const rateMutation = useMutation({
    mutationFn: ({ rating }: { rating: number }) =>
      ratingsApi.rate(userId!, placeId, rating),
    onSuccess: () => {
      // Invalidate queries to refetch
      queryClient.invalidateQueries({ queryKey: ['userRatings', userId] });
      queryClient.invalidateQueries({ queryKey: ['place', placeId] });
    },
  });

  return {
    userRating: userRating?.rating || null,
    canRate: !userRating, // Can rate if user hasn't rated yet
    isLoading,
    rate: (rating: number) => rateMutation.mutate({ rating }),
    isSubmitting: rateMutation.isPending,
    error: rateMutation.error,
  };
};
